import React, { Component } from "react";
import {
  FaAppleAlt,
  FaBaby,
  FaHeartbeat,
  FaShieldAlt,
  FaTint,
} from "react-icons/fa";

class FAQSection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      visibleCount: 5,
      activeCategory: "Period",
      expandedIndexes: [],
      faqData: {
        Period: [
          {
            question: "How accurate is the period tracker?",
            answer:
              "Our tracker uses your cycle data to provide estimates, but accuracy may vary based on individual cycles.",
          },
          {
            question: "Can I use the tracker if my cycle is irregular?",
            answer:
              "Yes! The tracker adapts over time and gives helpful insights even with irregular periods.",
          },
          {
            question: "Will I get reminders for my upcoming period?",
            answer:
              "Yes, you can enable notifications to get timely reminders and updates.",
          },
        ],
        Ovulation: [
          {
            question: "How is ovulation calculated?",
            answer:
              "Ovulation is typically 14 days before your next period. The app calculates this based on your cycle history.",
          },
          {
            question: "What are common ovulation symptoms to look for?",
            answer:
              "Symptoms include light cramping, cervical mucus changes, and a slight increase in body temperature.",
          },
          {
            question: "Can I track fertile windows with this app?",
            answer:
              "Absolutely! The app highlights your most fertile days for family planning or contraception awareness.",
          },
        ],
        Pregnancy: [
          {
            question: "What features are available for pregnancy tracking?",
            answer:
              "Our tracker includes baby growth updates, weekly tips, and symptom logs from week 1 to 40.",
          },
          {
            question: "Can I log symptoms and doctor visits?",
            answer:
              "Yes! You can document everything from cravings to appointments in one place.",
          },
          {
            question:
              "Are there safe exercises and yoga suggestions for each trimester?",
            answer:
              "Definitely! We offer tailored yoga and fitness videos for each month of pregnancy.",
          },
        ],
        Safety: [
          {
            question: "Is this app medically approved or reviewed?",
            answer:
              "While built with expert insights, it is not a substitute for professional medical advice. Always consult your doctor.",
          },
          {
            question: "How can I get emergency contact support in the app?",
            answer:
              "You can find a safety section with emergency numbers and helplines for quick access.",
          },
          {
            question: "Can I get alerts for unsafe symptoms?",
            answer:
              "Yes! We notify you if any logged symptoms may need medical attention.",
          },
        ],
        Nutrition: [
          {
            question: "Are the nutrition tips based on medical guidelines?",
            answer:
              "Yes, we follow widely accepted dietary recommendations for menstrual and pregnancy health.",
          },
          {
            question: "Can I track water intake and supplements?",
            answer:
              "You sure can! Use our hydration and supplement log to stay on track.",
          },
          {
            question:
              "Are there meal plans for each phase of the menstrual or pregnancy cycle?",
            answer:
              "Yes, customized plans are provided depending on your phase or pregnancy month.",
          },
        ],
      },
    };
  }

  handleCategoryChange = (category) => {
    this.setState({
      activeCategory: category,
      visibleCount: 5,
      expandedIndexes: [],
    });
  };

  handleLoadMore = () => {
    this.setState((prevState) => ({
      visibleCount: this.state.faqData[prevState.activeCategory].length,
    }));
  };

  toggleAccordion = (index) => {
    this.setState((prevState) => {
      const isExpanded = prevState.expandedIndexes.includes(index);
      const expandedIndexes = isExpanded
        ? prevState.expandedIndexes.filter((i) => i !== index)
        : [...prevState.expandedIndexes, index];

      return { expandedIndexes };
    });
  };

  render() {
    const { activeCategory, visibleCount, faqData, expandedIndexes } =
      this.state;
    const currentFAQs = faqData[activeCategory];
    const visibleFAQs = currentFAQs.slice(0, visibleCount);

    return (
      <section className="py-12 font-nunito px-4 bg-pink-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl text-center font-cormorant font-bold text-pink-600 mb-2">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-600 text-center mb-8">
            Find answers to common questions about pregnancy tracking and
            women's health.
          </p>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 justify-center mb-8">
            {Object.keys(faqData).map((category) => {
              const icons = {
                Period: <FaTint className="inline mr-1 text-red-500" />,
                Ovulation: (
                  <FaHeartbeat className="inline mr-1 text-pink-400" />
                ),
                Pregnancy: <FaBaby className="inline mr-1 text-purple-500" />,
                Safety: <FaShieldAlt className="inline mr-1 text-yellow-500" />,
                Nutrition: (
                  <FaAppleAlt className="inline mr-1 text-green-500" />
                ),
              };

              return (
                <button
                  key={category}
                  onClick={() => this.handleCategoryChange(category)}
                  className={`flex items-center gap-1 px-4 py-2 rounded-full text-sm font-medium transition ${
                    activeCategory === category
                      ? "bg-pink-600 text-white"
                      : "bg-white text-gray-700 hover:bg-pink-100"
                  }`}
                >
                  {icons[category]} {category}
                </button>
              );
            })}
          </div>

          {/* Accordion Items */}
          <div className="space-y-4">
            {visibleFAQs.map((faq, index) => {
              const isOpen = expandedIndexes.includes(index);
              return (
                <div
                  key={index}
                  className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition"
                >
                  <button
                    onClick={() => this.toggleAccordion(index)}
                    className="flex justify-between w-full text-left"
                  >
                    <h3 className="text-lg font-semibold text-gray-800">
                      {faq.question}
                    </h3>
                    <span className="text-pink-600 text-xl">
                      {isOpen ? "−" : "+"}
                    </span>
                  </button>
                  {isOpen && (
                    <p className="text-gray-600 mt-3 transition-all duration-300">
                      {faq.answer}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Load More Button */}
          {visibleCount < currentFAQs.length && (
            <div className="text-center mt-6">
              <button
                onClick={this.handleLoadMore}
                className="px-6 py-2 bg-pink-100 text-pink-600 rounded-full hover:bg-pink-200 transition"
              >
                Load More Questions
              </button>
            </div>
          )}
        </div>
      </section>
    );
  }
}

export default FAQSection;
